import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import emailService from '../services/emailService';
import { decodeEmailHeader, formatDate } from '../utils/emailUtils';
import './EmailList.css';
import './EmailList.css';

function EmailList() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [exportLoading, setExportLoading] = useState({
    csv: false,
    pdf: false,
    all: false
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Initialiser la recherche depuis l'URL au chargement
  useEffect(() => {
    const urlSearchQuery = searchParams.get('search') || '';
    const urlPage = parseInt(searchParams.get('page')) || 1;
    
    setSearchQuery(urlSearchQuery);
    setCurrentPage(urlPage);
    
    // Charger les emails selon les paramètres URL
    if (urlSearchQuery) {
      handleSearchFromUrl(urlSearchQuery);
    } else {
      loadEmails(urlPage);
    }
  }, []);

  // Recharger quand la page change (seulement si pas de recherche)
  useEffect(() => {
    const urlSearchQuery = searchParams.get('search') || '';
    if (!urlSearchQuery) {
      loadEmails();
    }
  }, [currentPage]);

  const loadEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await emailService.getEmails(currentPage, 20);
      setEmails(data.emails || []);
      
      // Utiliser la pagination retournée par l'API
      if (data.pagination) {
        setTotalPages(data.pagination.pages || 1);
        setTotalEmails(data.pagination.total || 0);
      } else {
        // Fallback si pagination n'est pas disponible
        const total = data.total || 0;
        setTotalEmails(total);
        setTotalPages(Math.ceil(total / 20));
      }
    } catch (err) {
      setError(err.message);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchFromUrl = async (query, page = 1) => {
    if (!query.trim()) {
      loadEmails();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await emailService.searchEmails(query);
      setEmails(data.emails || []);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalEmails(data.emails?.length || 0);
    } catch (err) {
      setError(err.message);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      // Effacer la recherche et revenir à la liste normale
      setSearchParams({});
      loadEmails();
      return;
    }

    // Mettre à jour l'URL avec la recherche
    setSearchParams({ search: searchQuery, page: '1' });

    try {
      setLoading(true);
      setError(null);
      
      const data = await emailService.searchEmails(searchQuery);
      setEmails(data.emails || []);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalEmails(data.emails?.length || 0);
    } catch (err) {
      setError(err.message);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet email ?')) {
      return;
    }

    try {
      await emailService.deleteEmail(emailId);
      setEmails(emails.filter(email => email.id !== emailId));
      setTotalEmails(totalEmails - 1);
    } catch (err) {
      alert(`Erreur lors de la suppression: ${err.message}`);
    }
  };

  // Fonctions d'export
  const handleExportCSV = async (allEmails = false) => {
    const exportType = allEmails ? 'all' : 'csv';
    setExportLoading(prev => ({ ...prev, [exportType]: true }));
    
    try {
      await emailService.exportEmailsCSV(searchQuery, allEmails);
      // Notification de succès (optionnel)
    } catch (err) {
      alert(`Erreur lors de l'export CSV: ${err.message}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [exportType]: false }));
    }
  };

  const handleExportPDF = async (allEmails = false) => {
    const exportType = allEmails ? 'all' : 'pdf';
    setExportLoading(prev => ({ ...prev, [exportType]: true }));
    
    try {
      await emailService.exportEmailsPDF(searchQuery, allEmails);
      // Notification de succès (optionnel)
    } catch (err) {
      alert(`Erreur lors de l'export PDF: ${err.message}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [exportType]: false }));
    }
  };

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Fonction pour trier les emails
  const getSortedEmails = () => {
    if (!sortConfig.key) return emails;

    const sortedEmails = [...emails].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'sender':
          aValue = (a.sender_name || a.sender || '').toLowerCase();
          bValue = (b.sender_name || b.sender || '').toLowerCase();
          break;
        case 'subject':
          aValue = (decodeEmailHeader(a.subject) || '').toLowerCase();
          bValue = (decodeEmailHeader(b.subject) || '').toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.date || 0);
          bValue = new Date(b.date || 0);
          break;
        default:
          return 0;
      }

      if (sortConfig.key === 'date') {
        // Pour les dates, comparaison directe
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        // Pour les chaînes de caractères
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
    });

    return sortedEmails;
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return 'bi-arrow-down-up text-muted';
    }
    return sortConfig.direction === 'asc' ? 'bi-arrow-up text-primary' : 'bi-arrow-down text-primary';
  };

  return (
    <div className="container-fluid py-6">
      {/* Header */}
      <div className="page-header">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="page-title">
              <i className="bi bi-envelope mr-3"></i>
              Emails
            </h1>
            <p className="page-subtitle">
              Gérez et analysez vos emails importés ({totalEmails} emails au total)
            </p>
          </div>
          <Link to="/" className="btn btn-primary btn-lg">
            <i className="bi bi-plus-lg mr-2"></i>
            Importer des emails
          </Link>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="max-w-md">
          <form onSubmit={handleSearch} className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher dans les emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="btn btn-outline-primary" type="submit">
              <i className="bi bi-search"></i>
            </button>
            {searchQuery && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSearchParams({});
                  loadEmails();
                }}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Export Buttons */}
      {!loading && emails.length > 0 && (
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-outline-success btn-sm"
              onClick={() => handleExportCSV(false)}
              disabled={exportLoading.csv}
            >
              {exportLoading.csv ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Export CSV...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                  Export CSV ({searchQuery ? 'résultats' : 'page courante'})
                </>
              )}
            </button>
            
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleExportPDF(false)}
              disabled={exportLoading.pdf}
            >
              {exportLoading.pdf ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Export PDF...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf me-2"></i>
                  Export PDF ({searchQuery ? 'résultats' : 'page courante'})
                </>
              )}
            </button>
            
            <button
              className="btn btn-outline-info btn-sm"
              onClick={() => {
                if (window.confirm('Exporter tous les emails peut prendre du temps. Continuer ?')) {
                  handleExportCSV(true);
                }
              }}
              disabled={exportLoading.all}
            >
              {exportLoading.all ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Export en cours...
                </>
              ) : (
                <>
                  <i className="bi bi-download me-2"></i>
                  Exporter tous les emails (CSV)
                </>
              )}
            </button>
          </div>
          
          <div className="mt-2">
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              {searchQuery
                ? `Export des ${emails.length} emails trouvés pour "${searchQuery}"`
                : `Export de la page courante (${emails.length} emails)`
              }
            </small>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger mb-6" role="alert">
          <i className="bi bi-exclamation-triangle mr-2"></i>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="spinner-border text-blue-600 mb-4" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="text-slate-600">Chargement des emails...</p>
        </div>
      )}

      {/* Emails List */}
      {!loading && emails.length > 0 && (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort('sender')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span>Expéditeur</span>
                        <i className={`bi ${getSortIcon('sender')} ms-2`}></i>
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort('subject')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span>Sujet</span>
                        <i className={`bi ${getSortIcon('subject')} ms-2`}></i>
                      </div>
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => handleSort('date')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span>Date</span>
                        <i className={`bi ${getSortIcon('date')} ms-2`}></i>
                      </div>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedEmails().map(email => (
                    <tr key={email.id} className="email-item">
                      <td>
                        <div>
                          <div className="font-semibold text-slate-900">
                            {email.sender_name || email.sender}
                          </div>
                          {email.sender_name && (
                            <div className="small text-muted">{email.sender}</div>
                          )}
                          {email.recipient && (
                            <div className="small text-muted mt-1">
                              <span>À: {email.recipient}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/emails/${email.id}${searchQuery ? `?from=search&search=${encodeURIComponent(searchQuery)}` : ''}`}
                          className="text-decoration-none text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {decodeEmailHeader(email.subject) || '(Sans sujet)'}
                        </Link>
                      </td>
                      <td className="text-muted">
                        {email.date ? formatDate(email.date) : 'N/A'}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <Link
                            to={`/emails/${email.id}${searchQuery ? `?from=search&search=${encodeURIComponent(searchQuery)}` : ''}`}
                            className="btn btn-outline-primary"
                            title="Voir détails"
                          >
                            <i className="bi bi-eye"></i>
                          </Link>
                          <button 
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(email.id)}
                            title="Supprimer"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && emails.length === 0 && !error && (
        <div className="text-center py-12">
          <i className="bi bi-envelope text-6xl text-slate-300 mb-4 block"></i>
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucun email trouvé</h3>
          <p className="text-muted mb-6">
            {searchQuery ? 'Aucun résultat pour votre recherche.' : 'Commencez par importer des emails.'}
          </p>
          <Link to="/" className="btn btn-primary">
            <i className="bi bi-plus-lg mr-2"></i>
            Importer des emails
          </Link>
        </div>
      )}

      {/* Pagination */}
      {!loading && emails.length > 0 && totalPages > 1 && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-slate-600">
              Page {currentPage} sur {totalPages} ({emails.length} emails affichés)
            </div>
            <div className="text-sm text-slate-600">
              Total : {(currentPage - 1) * 20 + 1}-{Math.min(currentPage * 20, totalEmails)} sur {totalEmails} emails
            </div>
          </div>
          
          <nav className="flex justify-center">
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left mr-1"></i> Précédent
                </button>
              </li>
              
              {/* Affichage intelligent des pages */}
              {currentPage > 3 && (
                <>
                  <li className="page-item">
                    <button className="page-link" onClick={() => setCurrentPage(1)}>1</button>
                  </li>
                  {currentPage > 4 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                </>
              )}
              
              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                let page;
                if (totalPages <= 5) {
                  page = index + 1;
                } else if (currentPage <= 3) {
                  page = index + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + index;
                } else {
                  page = currentPage - 2 + index;
                }
                
                if (page >= 1 && page <= totalPages) {
                  return (
                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                      <button 
                        className="page-link"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                }
                return null;
              })}
              
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                  <li className="page-item">
                    <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                      {totalPages}
                    </button>
                  </li>
                </>
              )}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Suivant <i className="bi bi-chevron-right ml-1"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

export default EmailList; 